import {
  Divider,
  VStack,
  Flex,
  HStack,
  Spacer,
  Stack,
  Link,
} from '@chakra-ui/layout';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  Button,
  Tag,
  TagLabel,
  DrawerFooter,
  Skeleton,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { date as commonDate, finleyAssert } from '@finley-technologies/common';
import { Bank, CreditAgreement } from '@finley-technologies/finley-types';

import {
  useGetCreditFacilityMetricsQuery,
  useGetInvoiceByIdQuery,
  InvoicePaymentInKindItemFragmentGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { Typography } from 'packages/common-frontend/components/Typography';
import {
  formatCurrencyAmount,
  percentageToPercentageLabel,
  truncateUuid,
} from 'packages/common-frontend/utils';
import { PaymentInKindNoticeDownloadButton } from 'packages/invoices/ui/paymentInKind/InvoicePaymentInKindDownloadButton';
import { MarkAsNotifiedButton } from 'packages/invoices/ui/paymentInKind/MarkAsNotifiedButton';

export const InvoicePaymentInKindItemDrawer = ({
  isOpen,
  invoicePaymentInKindItem,
  facilityMechanics,
  onToggle,
}: {
  isOpen: boolean;
  invoicePaymentInKindItem: InvoicePaymentInKindItemFragmentGQLType;
  facilityMechanics: CreditAgreement.DomainEntity.FacilityMechanicsWithEntities;
  onToggle: (isOpen: boolean) => void;
}) => {
  const lenderCommitmentAmount = useMemo(() => {
    const capitalProviderEntity = facilityMechanics.parties.capitalProviders.find(
      (capProvider) =>
        capProvider.entity.id ===
        invoicePaymentInKindItem.capitalProviderOrganization.id,
    );

    finleyAssert(
      capitalProviderEntity,
      `[InvoicePaymentInKindItemDrawer] Missing capitalProviderEntity for ${_.startCase(
        invoicePaymentInKindItem.capitalProviderOrganization.name,
      )}`,
    );

    return capitalProviderEntity.commitmentAmount;
  }, [
    facilityMechanics.parties.capitalProviders,
    invoicePaymentInKindItem.capitalProviderOrganization.id,
    invoicePaymentInKindItem.capitalProviderOrganization.name,
  ]);

  const invoiceQuery = useGetInvoiceByIdQuery({
    variables: {
      invoiceId: invoicePaymentInKindItem.invoiceId,
    },
  });

  const reportDownload = useMemo(() => {
    return invoiceQuery.data?.getInvoiceById.invoiceReports?.find(
      (download) =>
        download.paymentInKindItemId === invoicePaymentInKindItem.id &&
        download.reportType === 'paymentInKindNotice',
    );
  }, [
    invoicePaymentInKindItem.id,
    invoiceQuery.data?.getInvoiceById.invoiceReports,
  ]);

  const { data: metricsDataRequest } = useGetCreditFacilityMetricsQuery({
    skip: !invoiceQuery.data,
    variables: {
      creditFacilityId: invoiceQuery?.data?.getInvoiceById
        .creditFacilityId as string,
      postingDate: commonDate.currentDateStr(),
    },
    fetchPolicy: 'no-cache',
  });

  const metrics = metricsDataRequest?.metrics;

  if (invoiceQuery.error) {
    throw new Error(
      `Failed to get PIK item for Disbursement. Please contact Finley support.`,
    );
  }

  if (!invoiceQuery.data?.getInvoiceById) {
    return null;
  }

  const invoice = invoiceQuery.data.getInvoiceById;

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={() => onToggle(false)}
      size="xl"
    >
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader mt="1.5rem" px="3rem" pb="0">
          <Typography variant="section-title" trim>
            PIK Notice for{' '}
            {_.startCase(
              invoicePaymentInKindItem.capitalProviderOrganization.name,
            )}
          </Typography>
        </DrawerHeader>
        <Divider my="1.5rem" w="100%" />
        <DrawerBody px="3rem">
          <VStack align="flex-start" gap="2rem">
            <HStack justifyContent="space-between" w="100%">
              {reportDownload && (
                <PaymentInKindNoticeDownloadButton report={reportDownload} />
              )}
              <Spacer />
              {(() => {
                if (invoicePaymentInKindItem.status === 'notified') {
                  return (
                    <Tag
                      variant="subtle"
                      colorScheme="green"
                      borderRadius="8px"
                      padding="8px 16px"
                    >
                      <Icon
                        icon="circle-check"
                        color={colors.green['300']}
                        style={{ marginRight: '8px' }}
                      />
                      <TagLabel>Notified</TagLabel>
                    </Tag>
                  );
                }
                return (
                  <MarkAsNotifiedButton
                    invoicePaymentInKindItemId={invoicePaymentInKindItem.id}
                  />
                );
              })()}
            </HStack>

            <Divider />
            <Typography variant="base-muted" trim>
              {/* @dani TODO: add capitalizedLoan.name to resolver to add here */}
              On {commonDate.fullDateStrToHumanDateString(invoice.dueDate)} the{' '}
              {_.startCase(invoice.loan?.name ?? 'loan')}&apos;s interest will
              be paid in kind, increasing the balance by{' '}
              {formatCurrencyAmount(
                invoicePaymentInKindItem.postServiceFeeTotalAmount,
                Bank.Currency.USD,
              )}
              .
            </Typography>

            <Divider />
            <Typography variant="section-subtitle" trim>
              PIK Details
            </Typography>
            <VStack w="100%" gap="1rem">
              {invoice.loan && (
                <HStack justifyContent="space-between" w="100%">
                  <Typography fontWeight="medium">
                    PIK Interest from{' '}
                    <Link
                      href={`/loans/${invoice.loan.id}`}
                      color={colors.blue['400']}
                    >
                      {_.startCase(invoice.loan.name)}
                    </Link>
                  </Typography>
                  <Typography variant="base-muted">
                    {formatCurrencyAmount(
                      invoicePaymentInKindItem.totalAmount,
                      Bank.Currency.USD,
                    )}
                  </Typography>
                </HStack>
              )}
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">
                  PIK to{' '}
                  <Link
                    href={`/loans/${invoicePaymentInKindItem.capitalizedLoanId}`}
                    color={colors.blue['400']}
                  >
                    Loan{' '}
                    {truncateUuid(invoicePaymentInKindItem.capitalizedLoanId)}
                  </Link>
                </Typography>
                <Typography variant="base-muted">
                  {formatCurrencyAmount(
                    invoicePaymentInKindItem.postServiceFeeTotalAmount,
                    Bank.Currency.USD,
                  )}
                </Typography>
              </HStack>
            </VStack>

            <Divider />
            <Typography variant="section-subtitle" trim>
              Facility Details
            </Typography>
            <VStack w="100%" gap="1rem">
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">Name</Typography>
                <Typography variant="base-muted">
                  {_.startCase(invoice.creditFacility.name)}
                </Typography>
              </HStack>
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">Borrower</Typography>
                <Typography variant="base-muted">
                  {_.startCase(invoice.paymentSourceOrganization.name)}
                </Typography>
              </HStack>
              {invoice.loan && (
                <HStack justifyContent="space-between" w="100%">
                  <Typography fontWeight="medium">Global Commitment</Typography>
                  <Typography variant="base-muted">
                    {formatCurrencyAmount(
                      invoice.loan.facility.commitmentAmount,
                      Bank.Currency.USD,
                    )}
                  </Typography>
                </HStack>
              )}
              <HStack alignItems="start" w="100%">
                <Typography fontWeight="medium">
                  {_.startCase(
                    invoicePaymentInKindItem.capitalProviderOrganization.name,
                  )}{' '}
                  Commitment
                </Typography>

                <Spacer />
                <Stack alignItems="end">
                  <Typography variant="base-muted">
                    {formatCurrencyAmount(
                      lenderCommitmentAmount.value,
                      lenderCommitmentAmount.currency,
                    )}
                  </Typography>
                  {invoice.loan && (
                    <Typography variant="base-muted">
                      (
                      {percentageToPercentageLabel(
                        (lenderCommitmentAmount.value /
                          invoice.loan.facility.commitmentAmount) *
                          100,
                        6,
                      )}{' '}
                      Pro Rata)
                    </Typography>
                  )}
                </Stack>
              </HStack>
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">
                  Global Outstanding Balance
                </Typography>
                {metrics ? (
                  <Typography variant="base-muted">
                    {formatCurrencyAmount(
                      metrics.outstandingPrincipalBalance,
                      Bank.Currency.USD,
                    )}
                  </Typography>
                ) : (
                  <Skeleton w="10rem" h="1rem" />
                )}
              </HStack>
            </VStack>
          </VStack>
        </DrawerBody>

        <DrawerFooter>
          <Flex justify="flex-end" w="100%" mb="2em">
            <Button
              as={RouterLink}
              to={`/loans/${invoicePaymentInKindItem.capitalizedLoanId}`}
              variant="v2-ghost"
              rightIcon={<Icon icon="chevron-right" />}
            >
              View Loan
            </Button>
          </Flex>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
